import { Box, Card, Paper, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ConnectionGif from 'assets/images/icons/connection_slow.gif';
// import Typography from 'components/Typography';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { brandColors } from 'types/colors';
import { Status } from 'types/status';
import { ReactComponent as Auto } from '../../assets/images/icons/Auto.svg';
import { ReactComponent as Cold } from '../../assets/images/icons/Cold.svg';
import { ReactComponent as Fan } from '../../assets/images/icons/controller.svg';
import { ReactComponent as Drop } from '../../assets/images/icons/Drop.svg';
import SOLAIBLACK from '../../assets/images/solaidark.png';
import { ReactComponent as Heat } from '../../assets/images/icons/Heat.svg';
import { ReactComponent as NoNetwork } from '../../assets/images/icons/NoNetwork.svg';
import ApartmentIcon from '@mui/icons-material/Apartment';

const PARAM_BOX_HEIGHT = { xs: '3rem', sm: '4rem' };
const SMALL_BOX_LABEL_SX = {
  fontSize: { xs: 12, sm: 12 },
  overflowWrap: 'anywhere',
  lineHeight: 1.25,
};
const SMALL_BOX_VALUE_SX = {
  fontSize: { xs: 15, sm: 16 },
  mt: { xs: 0, sm: 0.5 },
  overflowWrap: 'anywhere',
  fontWeight: 'bold',
  marginTop: { xs: '1px', sm: '4px' },
};
const POWER_ICON_SX = {
  color: '#fff',
  fontSize: { xs: 21, sm: 24 },
  width: { xs: 21, sm: 24 },
  height: { xs: 21, sm: 24 },
};
const INACTIVE_TIME = 900;

const parseLastRecordTimeMs = lastRecordTime => {
  if (lastRecordTime === null || lastRecordTime === undefined) {
    return null;
  }

  if (typeof lastRecordTime === 'number') {
    return lastRecordTime > 1000000000000 ? lastRecordTime : lastRecordTime * 1000;
  }

  const parsedMs = new Date(lastRecordTime).getTime();
  return Number.isFinite(parsedMs) ? parsedMs : null;
};

const isWithinCavSchedule = (cavStartTime, cavEndTime, now = new Date()) => {
  const parseSchedule = schedule => {
    if (!schedule) {
      return {};
    }
    if (typeof schedule === 'string') {
      try {
        return JSON.parse(schedule);
      } catch {
        return {};
      }
    }
    return typeof schedule === 'object' && !Array.isArray(schedule) ? schedule : {};
  };
  const timeToMinutes = value => {
    if (!value) {
      return null;
    }
    const [hours, minutes] = String(value).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    return hours * 60 + minutes;
  };
  const startSchedule = parseSchedule(cavStartTime);
  const endSchedule = parseSchedule(cavEndTime);

  if (Object.keys(startSchedule).length === 0 && Object.keys(endSchedule).length === 0) {
    return true;
  }

  const startMinutes = timeToMinutes(startSchedule[String(now.getDay())]);
  const endMinutes = timeToMinutes(endSchedule[String(now.getDay())]);
  if (startMinutes === null || endMinutes === null) {
    return false;
  }
  if (startMinutes === 0 && endMinutes === 0) {
    return true;
  }

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return startMinutes <= endMinutes
    ? startMinutes <= nowMinutes && nowMinutes <= endMinutes
    : nowMinutes >= startMinutes || nowMinutes <= endMinutes;
};

export const ControllerCard = props => {
  const [status, setStatus] = useState(Status.Disconnected);
  const [isHovered, setIsHovered] = useState(false);
  const [isInfoHovered, setIsInfoHovered] = useState(false);
  const [, setCavScheduleTick] = useState(0);
  const isXsScreen = useMediaQuery(theme => theme.breakpoints.only('xs'));
  const buildings = useSelector(state => {
    const userBuildings = state.users?.buildings;
    return Array.isArray(userBuildings) && userBuildings.length > 0 ? userBuildings : state.sensors?.buildings_details || [];
  });

  useEffect(() => {
    setStatus(props.status || Status.Disconnected);
  }, [props.status]);

  useEffect(() => {
    if (isInfoHovered) {
      document.body.classList.add('controller-card-hovered');
    } else {
      document.body.classList.remove('controller-card-hovered');
    }

    return () => {
      document.body.classList.remove('controller-card-hovered');
    };
  }, [isInfoHovered]);

  useEffect(() => {
    const intervalId = setInterval(() => setCavScheduleTick(tick => tick + 1), 60000);
    return () => clearInterval(intervalId);
  }, []);

  const navigate = useNavigate();
  const isRelayController = props.deviceType === 'Controller-relay-SEN54';
  const isBuildingUnassigned = props.building === null || props.building === undefined || props.building === '';
  const assignedBuilding = buildings.find(building => String(building?.id) === String(props.building_id));
  const isCavActiveNow =
    !assignedBuilding || isWithinCavSchedule(assignedBuilding.cav_start_time, assignedBuilding.cav_end_time);
  const isOutsideCavHours = Boolean(assignedBuilding) && !isCavActiveNow;

  const handleSensorClick = event => {
    if (props.onCardClick) {
      props.onCardClick(event);
    } else {
      navigate(`/controller/${props.id}`);
    }
  };

  const lastRecordTimeMs = parseLastRecordTimeMs(props.params?.last_record_time);
  const active = lastRecordTimeMs !== null && Date.now() - lastRecordTimeMs < INACTIVE_TIME * 1000;
  const channelLabel = value => {
    if (value === 0 || value === '0') return 'OFF';
    if (value === 1 || value === '1') return 'ON';
    return 'N/A';
  };
  const channelColor = value => {
    if (value === 0 || value === '0') return brandColors.RED;
    if (value === 1 || value === '1') return brandColors.GREEN;
    return '#1f2937';
  };
  const isOnValue = value => value === 1 || value === '1';
  const displayedChannel1 = isOutsideCavHours ? 0 : props.params.channel_1;
  const displayedChannel2 = isOutsideCavHours ? 0 : props.params.channel_2;
  const displayedOnOff = isOutsideCavHours ? 0 : props.params.on_off;
  const relayPowerOn = isOnValue(displayedChannel1) || isOnValue(displayedChannel2);
  const relayPowerAvailable =
    isOutsideCavHours ||
    (props.params.channel_1 !== null &&
      props.params.channel_1 !== undefined &&
      props.params.channel_2 !== null &&
      props.params.channel_2 !== undefined);
  const keypadLockLabel = value => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'LOCKED' : 'UNLOCKED';
    const normalized = String(value).trim().toLowerCase();
    if (['1', 'true', 't', 'yes', 'y', 'on', 'locked'].includes(normalized)) return 'LOCKED';
    if (['0', 'false', 'f', 'no', 'n', 'off', 'unlocked'].includes(normalized)) return 'UNLOCKED';
    return 'N/A';
  };
  const controllerModel = props.controller_model || 'N/A';
  const isControllerModelLong = controllerModel.length > 25;
  const controllerModelDisplay = isControllerModelLong ? `${controllerModel.slice(0, 25)}...` : controllerModel;
  const shouldSpinCardIcon = isRelayController ? relayPowerOn && active : isOnValue(displayedOnOff) && active;
  const controlMode = props.params.control;
  const isSolaiControl = controlMode === 'solai';
  const displayedMode = String(props.params.mode ?? '').trim().toLowerCase();
  const relayModeDisplay =
    isRelayController && isSolaiControl
      ? (props.comfort ?? 'N/A')
      : displayedMode === 'fan'
        ? 'Fan'
        : displayedMode === 'cool'
          ? 'Cool'
          : displayedMode === 'heat'
            ? 'Heat'
            : displayedMode === 'auto'
              ? 'Auto'
              : displayedMode === 'dry'
                ? 'Dry'
                : 'N/A';

  return (
    <Card
      onClick={handleSensorClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        zIndex: isInfoHovered ? 1301 : 'auto',
        overflow: 'visible',
        borderRadius: 4,
        width: {
          xs: '100%',
          sm: 'fit-content',
          '&:hover': {
            cursor: 'pointer',
            boxShadow: 8,
          },
        },
        '&:hover': {
          cursor: 'pointer',
          boxShadow: isInfoHovered ? '0 18px 44px rgba(0,0,0,0.28)' : '0 4px 20px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        },
        transition: 'all 0.2s ease-in-out',
      }}>
      {props.noticeIcon && (
        <Tooltip title={props.noticeTooltip ?? ''} arrow>
          <IconButton
            size="small"
            aria-label="controller notice"
            onClick={event => event.stopPropagation()}
            sx={{
              position: 'absolute',
              top: { xs: -12, sm: -14 },
              left: { xs: -12, sm: -14 },
              zIndex: 2,
              width: { xs: 24, sm: 30 },
              height: { xs: 24, sm: 30 },
              color: brandColors.BLUE,
              backgroundColor: '#ffffff',
              border: '1px solid #dde6ff',
              boxShadow: ({ boxShadows }) => boxShadows.sm,
              '&:hover': {
                color: '#ffffff',
                backgroundColor: brandColors.BLUE,
                borderColor: brandColors.BLUE,
              },
            }}>
            {props.noticeIcon}
          </IconButton>
        </Tooltip>
      )}
      {isBuildingUnassigned && (
        <Tooltip
          title="This controller is not assigned to a building, so no CAV operating hours are configured. It may therefore remain active 24 hours a day, 7 days a week."
          arrow>
          <IconButton
            size="small"
            aria-label="Controller has no configured CAV operating hours"
            onClick={event => event.stopPropagation()}
            sx={{
              position: 'absolute',
              top: { xs: -12, sm: -14 },
              right: { xs: -12, sm: -14 },
              zIndex: 2,
              width: { xs: 24, sm: 30 },
              height: { xs: 24, sm: 30 },
              color: brandColors.RED,
              backgroundColor: `#ffffff`,
              border: `1px solid ${brandColors.RED}`,
              boxShadow: ({ boxShadows }) => boxShadows.sm,
              '&:hover': {
                color: '#ffffff',
                backgroundColor: brandColors.RED,
                borderColor: brandColors.RED,
              },
            }}>
            <PriorityHighIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        ml={{ xs: 0.5, sm: 1 }}
        mr={{ xs: 0.2, sm: 1.25 }}
        my={-0.5}
        sx={{
          padding: { xs: '8px 0px 0px 0px', sm: '12px 0px 4px 0px' },
        }}>
        <Box className="container" sx={{ width: '100%', minWidth: 0 }}>
          <Box
            className="topContainer"
            sx={{
              display: 'flex',
              width: { xs: '100%', sm: '19.5rem' },
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                minWidth: 0,
                pr: { xs: '30px', sm: '48px' },
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
                <Paper
                  className="sensorStatusIcon"
                  color={'white'}
                  elevation={0}
                  style={{
                    background: `${active ? brandColors.BLUE : brandColors.GREY}`,
                  }}
                  sx={{
                    padding: { xs: 0.4, sm: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: { xs: '1.7rem', sm: '3rem' },
                    width: { xs: '1.7rem', sm: '3rem' },
                    height: { xs: '1.7rem', sm: '3rem' },
                    flexShrink: 0,
                    borderRadius: 2.5,
                  }}>
                  <SvgIcon
                    sx={{
                      color: '#fff',
                      scale: { xs: 1.6, sm: 2.7 },
                      '& .card-icon': {
                        display: 'block',
                        width: '1em',
                        height: '1em',
                        animation: shouldSpinCardIcon ? 'controllerCardFanSpin 0.5s linear infinite' : 'none',
                        transformBox: 'view-box',
                        transformOrigin: '50% 50%',
                      },
                      '@keyframes controllerCardFanSpin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}>
                    <Fan className="card-icon" />
                  </SvgIcon>
                </Paper>
                <Typography
                  fontSize={{ xs: 14, sm: 17 }}
                  sx={{
                    overflowWrap: { xs: 'normal', sm: 'anywhere' },
                    marginLeft: { xs: 0.75, sm: 1 },
                    fontWeight: 600,
                    color: '#000',
                    lineHeight: { xs: 1.25, sm: 1.5 },
                    minWidth: 0,
                    flex: 1,
                    maxWidth: '100%',
                    whiteSpace: { xs: 'nowrap', sm: 'normal' },
                    overflow: { xs: 'hidden', sm: 'visible' },
                    textOverflow: { xs: 'ellipsis', sm: 'clip' },
                  }}>
                  {props.name ? `${props.name}` : props.id}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 6, sm: 12 },
                right: { xs: 4, sm: 12 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 18, sm: 30 },
                height: { xs: 18, sm: 30 },
                pointerEvents: 'none',
              }}>
              {!active ? (
                <SvgIcon
                  sx={{
                    color: brandColors.Black,
                    scale: { xs: 1.1, sm: 1.4 },
                  }}>
                  <NoNetwork className="HomeIconClass" />
                </SvgIcon>
              ) : (
                <img src={ConnectionGif} alt="connected" style={{ width: '100%', height: 'auto' }} />
              )}
            </Box>
          </Box>
          <Box
            className="location-row"
            sx={{
              marginTop: '.5rem',
              width: { xs: '100%', sm: '19rem' },
              paddingBottom: '.3rem',
              '& .location-label': { display: 'none' },
              '&:hover .location-label': { display: 'inline' },
            }}>
            <Typography
              fontSize={{ xs: 12, sm: 13 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'regular',
                color: '#000',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}>
              <ApartmentIcon sx={{ scale: 1.3, mr: 0.5, ml: 0.2, flexShrink: 0, color: '#000000b0' }} />
              <Box component="span" className="location-label">
                Building:{' '}
              </Box>
              {props.building === null ? 'Unassigned' : props.building}
              {props.floorNumber === null ? (
                ''
              ) : (
                <>
                  {' / '}
                  <Box component="span" className="location-label">
                    Floor:{' '}
                  </Box>
                  {props.floorNumber}
                </>
              )}
              {props.area_name === 'floor' ? (
                ' '
              ) : (
                <>
                  {' / '}
                  <Box component="span" className="location-label">
                    Area:{' '}
                  </Box>
                  {props.area_name}
                </>
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
      {active && props.displayData && (
        <Box
          sx={{
            // border: '2px solid red',
            display: 'flex',
            padding: { xs: '6px', sm: '.5rem' },
            gap: { xs: 0.5, sm: '.5rem' },
            alignItems: 'center',
          }}>
          {isRelayController ? (
            <>
              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 2.5,
                  display: 'flex',
                  height: PARAM_BOX_HEIGHT,
                  width: '33.333%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: brandColors.GREY_LIGHT,
                  alignItems: 'center',
                }}>
                <Typography className="small-box-label" sx={SMALL_BOX_LABEL_SX}>
                  POWER
                </Typography>
                {relayPowerAvailable ? (
                  <Box
                    className="small-box-value"
                    sx={{
                      ...SMALL_BOX_VALUE_SX,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 22, sm: 26 },
                      height: { xs: 22, sm: 26 },
                      borderRadius: '50%',
                      backgroundColor: relayPowerOn ? brandColors.GREEN : brandColors.GREY,
                    }}>
                    <PowerSettingsNewIcon sx={POWER_ICON_SX} />
                  </Box>
                ) : (
                  <Typography className="small-box-value" sx={{ ...SMALL_BOX_VALUE_SX, color: '#1f2937' }}>
                    N/A
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 2.5,
                  minWidth: { xs: 0, sm: '84px' },
                  display: 'flex',
                  height: PARAM_BOX_HEIGHT,
                  width: '33.333%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: brandColors.GREY_LIGHT,
                  alignItems: 'center',
                }}>
                <Typography className="small-box-label" sx={SMALL_BOX_LABEL_SX}>
                  CONTROL
                </Typography>
                {isSolaiControl && (
                  <Box
                    component="img"
                    src={SOLAIBLACK}
                    className="HomeIconClass"
                    alt="SOLIN"
                    sx={{
                      width: { xs: 38, sm: 45 },
                      marginTop: { xs: '.4rem', sm: '.6rem' },
                    }}
                  />
                )}
                {controlMode === 'manual' && (
                  <Typography
                    className="small-box-value"
                    sx={{
                      ...SMALL_BOX_VALUE_SX,
                      color: '#000',
                      textAlign: 'center',
                      marginTop: { xs: '1px', sm: '4px' },
                    }}>
                    MANUAL
                  </Typography>
                )}
                {controlMode === 'schedule' && (
                  <Typography
                    className="small-box-value"
                    sx={{
                      ...SMALL_BOX_VALUE_SX,
                      color: '#000',
                      textAlign: 'center',
                      marginTop: { xs: '1px', sm: '4px' },
                    }}>
                    SCHEDULE
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 2.5,
                  minWidth: { xs: 0, sm: '84px' },
                  display: 'flex',
                  height: PARAM_BOX_HEIGHT,
                  width: '33.333%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: brandColors.GREY_LIGHT,
                  alignItems: 'center',
                }}>
                <Typography className="small-box-label" sx={SMALL_BOX_LABEL_SX}>
                  MODE
                </Typography>
                <SvgIcon
                  sx={{
                    scale: { xs: 1.6, sm: 2 },
                    marginTop: { xs: 0, sm: '.5rem' },
                    color:
                      displayedMode === 'cool' ? brandColors.BLUE : displayedMode === 'heat' ? brandColors.RED : brandColors.BLACK,
                  }}>
                  {displayedMode === 'fan' && <Fan className="HomeIconClass" />}
                  {displayedMode === 'cool' && <Cold className="HomeIconClass" />}
                  {displayedMode === 'heat' && <Heat className="HomeIconClass" />}
                  {displayedMode === 'auto' && <Auto className="HomeIconClass" />}
                  {displayedMode === 'dry' && <Drop className="HomeIconClass" />}
                </SvgIcon>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 2.5,
                  display: 'flex',
                  height: PARAM_BOX_HEIGHT,
                  width: '33.333%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: brandColors.GREY_LIGHT,
                  alignItems: 'center',
                }}>
                <Typography className="small-box-label" sx={SMALL_BOX_LABEL_SX}>
                  POWER
                </Typography>
                <Box
                  className="small-box-value"
                  sx={{
                    ...SMALL_BOX_VALUE_SX,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 22, sm: 26 },
                    height: { xs: 22, sm: 26 },
                    borderRadius: '50%',
                    backgroundColor: isOnValue(displayedOnOff) ? brandColors.GREEN : brandColors.GREY,
                  }}>
                  <PowerSettingsNewIcon sx={POWER_ICON_SX} />
                </Box>
              </Box>

              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 2.5,
                  minWidth: { xs: 0, sm: '84px' },
                  display: 'flex',
                  height: PARAM_BOX_HEIGHT,
                  width: '33.333%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: brandColors.GREY_LIGHT,
                  alignItems: 'center',
                }}>
                <Typography className="small-box-label" sx={SMALL_BOX_LABEL_SX}>
                  CONTROL
                </Typography>
                {isSolaiControl && (
                  <Box
                    component="img"
                    src={SOLAIBLACK}
                    className="HomeIconClass"
                    alt="low"
                    sx={{
                      width: { xs: 38, sm: 45 },
                      marginTop: { xs: '.2rem', sm: '.6rem' },
                    }}
                  />
                )}
                {controlMode === 'manual' && (
                  <Typography
                    className="small-box-value"
                    sx={{
                      ...SMALL_BOX_VALUE_SX,
                      color: '#000',
                      textAlign: 'center',
                      marginTop: { xs: '1px', sm: '4px' },
                    }}>
                    MANUAL
                  </Typography>
                )}
                {controlMode === 'schedule' && (
                  <Typography
                    className="small-box-value"
                    sx={{
                      ...SMALL_BOX_VALUE_SX,
                      color: '#000',
                      textAlign: 'center',
                      marginTop: { xs: '1px', sm: '4px' },
                    }}>
                    SCHEDULE
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 2.5,
                  minWidth: { xs: 0, sm: '84px' },
                  display: 'flex',
                  height: PARAM_BOX_HEIGHT,
                  width: '33.333%',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  backgroundColor: brandColors.GREY_LIGHT,
                  alignItems: 'center',
                }}>
                <Typography className="small-box-label" sx={SMALL_BOX_LABEL_SX}>
                  MODE
                </Typography>
                <SvgIcon
                  sx={{
                    scale: { xs: 1.6, sm: 2 },
                    marginTop: { xs: 0, sm: '.5rem' },
                    color:
                      displayedMode === 'cool' ? brandColors.BLUE : displayedMode === 'heat' ? brandColors.RED : brandColors.BLACK,
                    // border: "1px solid green",
                  }}>
                  {displayedMode === 'fan' && <Fan className="HomeIconClass" />}
                  {displayedMode === 'cool' && <Cold className="HomeIconClass" />}
                  {displayedMode === 'heat' && <Heat className="HomeIconClass" />}
                  {displayedMode === 'auto' && <Auto className="HomeIconClass" />}
                  {displayedMode === 'dry' && <Drop className="HomeIconClass" />}
                </SvgIcon>
              </Box>
            </>
          )}
        </Box>
      )}
      {active && props.displayData && !isXsScreen && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
            maxHeight: isHovered ? 44 : 0,
            opacity: isHovered ? 1 : 0,
            pb: isHovered ? 0.5 : 0,
            transition: 'max-height 220ms ease, opacity 180ms ease, padding 220ms ease',
          }}>
          <IconButton
            size="small"
            aria-label="controller info"
            onClick={event => event.stopPropagation()}
            onMouseEnter={() => setIsInfoHovered(true)}
            onMouseLeave={() => setIsInfoHovered(false)}
            sx={{
              color: brandColors.BLUE,
              backgroundColor: '#f4f7ff',
              border: '1px solid #dde6ff',
              transition: 'all 180ms ease',
              '&:hover': {
                color: '#ffffff',
                backgroundColor: brandColors.BLUE,
                borderColor: brandColors.BLUE,
              },
            }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {active && props.displayData && !isXsScreen && (
        <Box
          className="info-box"
          sx={{
            width: '100%',
            mt: 0,
            backgroundColor: '#ffffff',
            borderRadius: '0 0 12px 12px',
            px: 1.25,
            py: isInfoHovered ? 1 : 0,
            maxHeight: isInfoHovered ? 220 : 0,
            overflow: 'hidden',
            transition: 'max-height 220ms ease, padding 220ms ease',
          }}
          onClick={event => event.stopPropagation()}
          onMouseEnter={() => setIsInfoHovered(true)}
          onMouseLeave={() => setIsInfoHovered(false)}>
          <Typography
            fontSize={16}
            sx={{
              color: '#1f2937',
              pb: 0.4,
              borderBottom: '1px solid #e5e7eb',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}>
            Model:{' '}
            <Tooltip title={isControllerModelLong ? controllerModel : ''}>
              <Box component="span">{controllerModelDisplay}</Box>
            </Tooltip>
          </Typography>
          {!isRelayController && (
            <>
              <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4, pb: 0.4, borderBottom: '1px solid #e5e7eb' }}>
                Comfort mode: {props.comfort ?? 'N/A'}
              </Typography>
              <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4, pb: 0.4, borderBottom: '1px solid #e5e7eb' }}>
                Cool valve:{' '}
                {props.params.cool_water_valve !== undefined && props.params.cool_water_valve !== null
                  ? `${props.params.cool_water_valve}%`
                  : 'N/A'}
              </Typography>
              <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4, pb: 0.4, borderBottom: '1px solid #e5e7eb' }}>
                Keypad lock: {keypadLockLabel(props.params.keypad_lock)}
              </Typography>
            </>
          )}
          {isRelayController && (
            <>
              <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4, pb: 0.4, borderBottom: '1px solid #e5e7eb' }}>
                Channel 1:{' '}
                <Box component="span" sx={{ color: channelColor(displayedChannel1), fontWeight: 'bold' }}>
                  {channelLabel(displayedChannel1)}
                </Box>
              </Typography>
              <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4, pb: 0.4, borderBottom: '1px solid #e5e7eb' }}>
                Channel 2:{' '}
                <Box component="span" sx={{ color: channelColor(displayedChannel2), fontWeight: 'bold' }}>
                  {channelLabel(displayedChannel2)}
                </Box>
              </Typography>
              <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4, pb: 0.4, borderBottom: '1px solid #e5e7eb' }}>
                Comfort: {props.comfort ?? 'N/A'}
              </Typography>
            </>
          )}
          <Typography fontSize={16} sx={{ color: '#1f2937', mt: 0.4 }}>
            Occupancy:{' '}
            {props.params.occupancy !== undefined && props.params.occupancy !== null ? `${props.params.occupancy}%` : 'N/A'}
          </Typography>
        </Box>
      )}
    </Card>
    // </Link>
  );
};
